import React, { useState, useEffect } from 'react';
import './Input.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Input() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [processing, setProcessing] = useState(false);
    const [username, setUsername] = useState("");
    const [experimentName, setExperimentName] = useState("");
    const [uid, setUid] = useState("");
    const [selectedExperiment, setSelectedExperiment] = useState(null);

    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExperimentId, setSelectedExperimentId] = useState(null);
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);
    const [showScenarios, setShowScenarios] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");




    useEffect(() => {
        const storedUsername = localStorage.getItem('username');
        const storedUid = localStorage.getItem('uid');
        if (storedUsername) {
            setUsername(storedUsername);
        }
        if (storedUid) {
            setUid(storedUid);
        }
        else {
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        if (uid) {
            fetchExperiments();
        }
    }, [uid]);

    const fetchExperiments = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/users/${uid}/favorites`);
            if (response.data.success) {
                setFavorites(response.data.favorites);
            } else {
                setErrorMessage("Failed to fetch experiments: " + response.data.message);
                setShowErrorPopup(true);
                setFavorites([]);
            }
        } catch (error) {
            setErrorMessage("Error fetching experiments. Please try again.");
            setShowErrorPopup(true);
            setFavorites([]);
        }
        finally {
            setLoading(false);
        }
    };


    const fetchScenariosForExperiment = async (experimentId) => {
        if (!experimentId || !uid) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/users/${uid}/favorites/${experimentId}/scenarios`);
            if (response.data.success) {
                setScenarios(response.data.scenarios);
            } else {
                console.error("Failed to fetch scenarios:", response.data.message);
                setScenarios([]);
            }
        } catch (error) {
            console.error("Error fetching scenarios:", error);
            setScenarios([]);
        } finally {
            setLoading(false);
        }

    };

    const handleExperimentCheckboxChange = (id, experiment) => {
        if (selectedExperimentId === id) {
            setSelectedExperimentId(null);
            setSelectedExperiment(null);
        } else {
            setSelectedExperimentId(id);
            setSelectedExperiment({ id, ...experiment });
            fetchScenariosForExperiment(id);
        }
    };

    const handleExperimentClick = (experiment) => {
        setSelectedExperimentId(experiment.id);
        setSelectedExperiment(experiment);
        fetchScenariosForExperiment(experiment.id);
    };


    const handleSelectExperiment = () => {
        if (selectedExperimentId) {
            setShowScenarios(true);
            setSelectedScenarioId(null); // Clear any previous scenario selection
        }
    };

    const handleScenarioCheckboxChange = (id) => {
        // Toggle selection
        if (selectedScenarioId === id) {
            setSelectedScenarioId(null);
        } else {
            setSelectedScenarioId(id);
            const selectedScenarioObj = scenarios.find(s => s.id === id);
        }
    };

    const handleModeSelection = (mode, experimentId, scenarioId) => {
        if (!selectedScenarioId) return;

        // Navigate to the selected mode with the scenario ID
        if (mode === 'planning') {
            navigate(`/planning/${uid}/${experimentId}/${scenarioId}`);
        } else if (mode === 'operation') {
            navigate(`/operation/${uid}/${experimentId}/${scenarioId}`);
        }
    };

    function SuccessPopup({ message, onClose }) {
        return (
            <div className="popup-overlay" onClick={onClose}>
                <div className="success-popup" onClick={e => e.stopPropagation()}>
                    <div className="success-icon">✓</div>
                    <h3>{message}</h3>
                    <button className="close-popup-button" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        );
    }

    function ErrorPopup({ message, onClose }) {
        return (
            <div className="popup-overlay" onClick={onClose}>
                <div className="error-popup" onClick={e => e.stopPropagation()}>
                    <div className="error-icon">✕</div>
                    <h3>{message}</h3>
                    <button className="close-popup-button" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        );
    }

    async function handleUpload() {
        if (!file) {
            setErrorMessage("Please select a file first");
            setMessage("Please select a file first");
            return;
        }

        if (!experimentName.trim()) {
            setErrorMessage("Please enter an experiment name");
            setShowErrorPopup(true);
            return;
        }

        setUploading(true);
        setMessage("Uploading file and generating scenarios...");
        setProcessing(true);

        // Create FormData object
        const formData = new FormData();
        formData.append('file', file);
        formData.append('experimentName', experimentName);
        formData.append('username', username);
        formData.append('uid', uid);



        try {
            const response = await axios.post('${API_BASE_URL}/upload', formData);

            if (response.data.success) {
                setSuccessMessage("Scenarios and optimization results were generated successfully");
                setShowSuccessPopup(true);

                setMessage(`File processed successfully! ${response.data.total_saved} scenarios created.`);
                fetchExperiments();
            } else {
                setErrorMessage(response.data.message || "Processing completed but with issues");
                setMessage("Processing completed but with issues. See console for details.");
            }
        } catch (error) {
            const detail = error.response?.data?.message || error.message || "Please check the console for more details.";
            const userMessage = `Error processing file: ${detail}`;
            setErrorMessage(userMessage);
            setMessage(userMessage);
            setShowErrorPopup(true);
            console.error("Upload error:", error);
        } finally {
            setUploading(false);
            setProcessing(false);
        }
    }

    return (
        <div className="input-page-container">
            <div className="input-section" >
                {username && (
                    <div className="welcome-message">
                        <h2>Welcome, {username}!</h2>
                    </div>
                )}
                <div className="input-container">
                    <h1>Upload Input File</h1>

                    <div className="input-form">
                        <div className="experiment-name-input">
                            <label htmlFor="experiment-name">Experiment Name:</label>
                            <input
                                id="experiment-name"
                                type="text"
                                value={experimentName}
                                onChange={(e) => setExperimentName(e.target.value)}
                                placeholder="Enter experiment name"
                                className="text-input"
                            />
                        </div>

                        <div className="file-input-wrapper">
                            <input
                                type="file"
                                onChange={(e) => setFile(e.target.files[0])}
                                className="file-input"
                                accept=".json,.xlsx,.csv"
                            />
                            <span className="file-name">{file ? file.name : 'No file selected'}</span>
                        </div>

                        <button
                            onClick={handleUpload}
                            className="upload-button"
                            disabled={uploading || !file}
                        >
                            {uploading ? 'Processing...' : 'Upload & Generate Scenarios'}
                        </button>

                        {message && <div className={`message ${processing ? 'processing' : ''}`}>{message}</div>}

                        {processing && (
                            <div className="processing-indicator">
                                <div className="spinner"></div>
                                <p>Processing file and generating scenarios...</p>
                                <p>This may take a few minutes.</p>
                            </div>
                        )}
                    </div>

                    <div className="manual-data">
                        <button onClick={() => navigate("/manual_input")}>Enter Data Manually</button>
                    </div>

                </div>
            </div>

            <div className="experiments-list">
                <h2>{showScenarios ? `Scenarios for ${selectedExperiment?.name}` : "My Favorites"}</h2>

                {loading && <div className="spinner"></div>}

                <div className="selection-container">
                    {!showScenarios ? (
                        // Experiment Selection View
                        <div className="experiments-container">
                            {!favorites || Object.keys(favorites).length === 0 ? (
                                <p className="no-data-message">No experiments found. Upload a file or enter data manually to create one.</p>
                            ) : (
                                <div className="selection-list">
                                    {Object.entries(favorites).map(([id, experiment]) => (
                                        <div
                                            key={id}
                                            className={`selection-item ${selectedExperimentId === id ? 'selected' : ''}`}
                                            onClick={() => handleExperimentCheckboxChange(id, experiment)}
                                        >
                                            <div className="checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    id={`experiment-${id}`}
                                                    checked={selectedExperimentId === id}
                                                    onChange={() => handleExperimentCheckboxChange(id, experiment)}
                                                />
                                                <label htmlFor={`experiment-${id}`} className="checkbox-label"></label>
                                            </div>
                                            <div
                                                className="selection-details"
                                                onClick={() => handleExperimentClick({ id, ...experiment })}
                                            >
                                                <h3>{experiment.name || "Unnamed Experiment"}</h3>
                                                <p>Created: {experiment.created_at ? new Date(experiment.created_at).toLocaleDateString() : "Unknown date"}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="selection-actions">
                                <button
                                    className="action-button"
                                    disabled={!selectedExperimentId}
                                    onClick={handleSelectExperiment}
                                >
                                    Select Experiment
                                </button>
                                <button
                                    className="secondary-button"
                                    onClick={fetchExperiments}
                                >
                                    Refresh List
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Scenario Selection View
                        <div className="scenarios-container">
                            {scenarios.length === 0 ? (
                                <div className="no-scenarios">
                                    <p>No scenarios found for this experiment.</p>
                                    <button
                                        className="secondary-button"
                                        onClick={() => setShowScenarios(false)}
                                    >
                                        Back to Experiments
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="selection-list">
                                        {scenarios.map((scenario) => (
                                            <div
                                                key={scenario.id}
                                                className={`selection-item ${selectedScenarioId === scenario.id ? 'selected' : ''}`}
                                                onClick={() => handleScenarioCheckboxChange(scenario.id)}
                                            >
                                                <div className="checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        id={`scenario-${scenario.id}`}
                                                        checked={selectedScenarioId === scenario.id}
                                                        onChange={() => handleScenarioCheckboxChange(scenario.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <label htmlFor={`scenario-${scenario.id}`} className="checkbox-label"></label>
                                                </div>
                                                <div className="selection-details">
                                                    <h4>{scenario.name || `Scenario ${scenario.id}`}</h4>
                                                    <div className="scenario-summary">
                                                        <p><strong>Created:</strong> {new Date(scenario.created_at).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="selection-actions">
                                        <button
                                            className="action-button"
                                            disabled={!selectedScenarioId}
                                            onClick={() => handleModeSelection('planning', selectedExperimentId, selectedScenarioId)}
                                        >
                                            Planning
                                        </button>
                                        <button
                                            className="action-button"
                                            disabled={!selectedScenarioId}
                                            onClick={() => handleModeSelection('operation', selectedExperimentId, selectedScenarioId)}
                                        >
                                            Operation
                                        </button>
                                        <button
                                            className="secondary-button"
                                            onClick={() => setShowScenarios(false)}
                                        >
                                            Back to Experiments
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showSuccessPopup && (
                <SuccessPopup
                    message={successMessage}
                    onClose={() => setShowSuccessPopup(false)}
                />
            )}
            {showErrorPopup && (
                <ErrorPopup
                    message={errorMessage}
                    onClose={() => setShowErrorPopup(false)}
                />
            )}
        </div>

    );
};



